import React, { cloneElement, forwardRef, isValidElement } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils/cn';

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-full font-medium transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring focus-visible:ring-offset-2 button-press hover:scale-105 active:scale-95 no-underline",
  {
    variants: {
      variant: {
        solid: "bg-brand text-white hover:opacity-90",
        outline: "border border-brand text-brand hover:bg-brand/10",
        ghost: "text-brand hover:bg-brand/10",
        subtle: "bg-brand/10 text-brand hover:bg-brand/20",
        link: "text-brand underline hover:opacity-80",
      },
      size: {
        sm: "h-9 px-3 text-sm",
        md: "h-10 px-4 text-sm",
        lg: "h-11 px-5 text-base",
        icon: "h-10 w-10",
      },
      tone: {
        default: "bg-fg text-bg hover:opacity-90",
        primary: "bg-brand text-white hover:opacity-90",
        secondary: "bg-brand-secondary text-white hover:opacity-90",
        accent: "bg-accent text-accent-fg hover:opacity-90",
        success: "bg-success text-white hover:opacity-90",
        warning: "bg-warning text-warning-fg hover:opacity-90",
        danger: "bg-destructive text-white hover:opacity-90",
      },
      loading: {
        true: "pointer-events-none opacity-75",
        false: "",
      },
    },

      
    defaultVariants: {
      variant: "solid",
      size: "md",
      tone: "primary",
      loading: false,
    },
  }
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant, 
    size, 
    tone,
    loading = false,
    leftIcon,
    rightIcon,
    children,
    disabled,
    asChild = false,
    'aria-label': ariaLabel,
    ...props 
  }, ref) => {
    const hasTextContent = Boolean(children);
    const isIconOnly = size === 'icon' || (!hasTextContent && (leftIcon || rightIcon));

    if (process.env.NODE_ENV === 'development' && isIconOnly && !ariaLabel && !asChild) {
      console.warn(
        'Button: Icon-only buttons should have an aria-label for accessibility. ' +
        'Add an aria-label prop to describe the button\'s action.'
      );
    }

    const content = (innerChildren: React.ReactNode) => (
      <>
        {loading && (
          <svg
            className="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {!loading && leftIcon && (
          <span className="mr-2 flex-shrink-0" aria-hidden="true">{leftIcon}</span>
        )}
        {innerChildren}
        {!loading && rightIcon && (
          <span className="ml-2 flex-shrink-0" aria-hidden="true">{rightIcon}</span>
        )}
      </>
    );

    if (asChild) {
      if (!isValidElement(children)) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Button with `asChild` expects a single React element as its child.');
        }
        return (
          <button
            className={cn(buttonVariants({ variant, size, tone, loading }), className)}
            ref={ref}
            disabled={disabled === true || loading || undefined}
            aria-label={isIconOnly && !hasTextContent ? ariaLabel : ariaLabel}
            {...props}
          >
            {content(children)}
          </button>
        );
      }

      const child = children as React.ReactElement<{ className?: string; disabled?: boolean; children?: React.ReactNode }>;
      const mergedProps = {
        className: cn(buttonVariants({ variant, size, tone, loading }), child.props.className, className),
        disabled: disabled === true || loading || child.props.disabled,
        ref,
        'aria-label': isIconOnly && !hasTextContent ? ariaLabel : ariaLabel,
        ...props,
      } as unknown as React.Attributes & Record<string, unknown>;

      return cloneElement(
        child,
        mergedProps,
        content(child.props.children)
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, tone, loading }), className)}
        ref={ref}
        disabled={disabled === true || loading || undefined}
        aria-label={isIconOnly && !hasTextContent ? ariaLabel : ariaLabel}
        {...props}
      >
        {content(children)}
      </button>
    );
  }
);

Button.displayName = 'Button';

/** Solid destructive control: filled destructive color, white label, no border. Use after confirmation for irreversible deletes. */
export const destructiveButtonClassName = 'border-0 shadow-none';

/**
 * Neutral dismiss control for modals and confirm dialogs. Use with `variant="outline"`.
 * Destructive actions use {@link DestructiveButton} instead.
 */
export const dialogCancelButtonClassName =
  'border-border bg-card text-fg hover:bg-muted/30';

const DestructiveButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'variant' | 'tone'>
>(({ className, ...props }, ref) => (
  <Button
    ref={ref}
    variant="solid"
    tone="danger"
    className={cn(destructiveButtonClassName, className)}
    {...props}
  />
));
DestructiveButton.displayName = 'DestructiveButton';

export { DestructiveButton, Button, buttonVariants, type ButtonProps };
